import React, { ReactNode } from 'react';

type Condition = unknown;

export const Else: React.FC<{ children: React.ReactNode }> = ({ children }) => children;

export const ElseIf: React.FC<{
    condition: Condition | Condition[];
    mode?: 'all' | 'any';
    children: React.ReactNode;
}> = () => null; // Will be handled by the <If> parent

type IfProps = {
    condition: Condition | Condition[];
    mode?: 'all' | 'any';
    children: ReactNode;
};

function evaluate(condition: unknown | unknown[], mode: 'all' | 'any'): boolean {
    const isTruthy = (value: unknown) =>
        typeof value === 'boolean' ? value : value !== null && value !== undefined;

    if (Array.isArray(condition)) {
        return mode === 'any'
            ? condition.some(isTruthy)
            : condition.every(isTruthy);
    }

    return isTruthy(condition);
}

const If: React.FC<IfProps> = ({ condition, mode = 'all', children }) => {
    const primaryCondition = evaluate(condition, mode);

    if (primaryCondition) {
        // Render all children that are NOT Else or ElseIf
        const thenContent = React.Children.toArray(children).filter(
            (child: any) => !React.isValidElement(child) || (child.type !== Else && child.type !== ElseIf)
        );
        return thenContent;
    }

    // Loop through and find the first ElseIf or Else that matches
    let fallback: ReactNode = null;

    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;

        if (child.type === ElseIf) {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const { condition, mode = 'all', children: childChildren } = child.props;
            if (evaluate(condition, mode)) {
                fallback = childChildren;
            }
        } else if (child.type === Else && fallback === null) {
            fallback = child.props.children;
        }
    });

    return fallback;
};

export default If;
